import { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";
import { listRdvOpenings, listRdvTypes, openRdvOffer, type RdvType } from "./rdv";
import type { CrmFiche } from "./types";

export function RdvSheet({ fiche, onClose, onSending, onError, onDone }: { fiche: CrmFiche; onClose: () => void; onSending?: (busy: boolean) => void; onError?: (message: string) => void; onDone?: (message: string) => void }) {
  const [types, setTypes] = useState<RdvType[]>([]);
  const [typeId, setTypeId] = useState("");
  const [days, setDays] = useState<{ key: string }[]>([]);
  const [day, setDay] = useState("");
  const [slots, setSlots] = useState<string[]>([]);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const sent = useRef(false);

  useEffect(() => {
    void listRdvTypes()
      .then((rows) => {
        setTypes(rows);
        setTypeId(rows[0]?.id || "");
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Mes RDV est indisponible."));
  }, []);

  useEffect(() => {
    const chosen = types.find((row) => row.id === typeId);
    if (!chosen) return;
    let alive = true;
    setLoading(true);
    setTime("");
    void listRdvOpenings(chosen.ownerId, chosen.id)
      .then((open) => {
        if (!alive) return;
        setDays(open.days);
        setDay(open.day);
        setSlots(open.slots);
      })
      .catch((reason: unknown) => {
        if (alive) setError(reason instanceof Error ? reason.message : "Créneaux indisponibles.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [typeId, types]);

  function pickDay(key: string) {
    const chosen = types.find((row) => row.id === typeId);
    if (!chosen || key === day) return;
    setDay(key);
    setTime("");
    setLoading(true);
    void listRdvOpenings(chosen.ownerId, chosen.id, key)
      .then((open) => {
        setDay(open.day || key);
        setSlots(open.slots);
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Créneaux indisponibles."))
      .finally(() => setLoading(false));
  }

  function send(close: () => void) {
    if (sent.current) return;
    const email = fiche.email.trim();
    const chosen = types.find((row) => row.id === typeId);
    if (!email.includes("@")) {
      setError("Renseignez l’e-mail du client sur la fiche.");
      return;
    }
    if (!chosen || !day || !time) {
      setError("Choisissez le type, le jour et l’heure.");
      return;
    }
    sent.current = true;
    close();
    onSending?.(true);
    void openRdvOffer({ ownerId: chosen.ownerId, eventTypeId: chosen.id, who: fiche.name, email, what: chosen.title, startsAt: `${day} ${time}` })
      .then(() => onDone?.("Le client a reçu le mail Mes RDV. Le rendez-vous entre dans l’agenda quand il clique sur Accepter."))
      .catch((reason: unknown) => onError?.(reason instanceof Error ? reason.message : "Le rendez-vous n’a pas été proposé."))
      .finally(() => onSending?.(false));
  }

  return (
    <Modal onClose={onClose} sheetClass="crm-pop w-full max-w-lg rounded-2xl border border-slate-200 p-5 shadow-[0_30px_80px_-36px_rgba(15,23,42,0.45)]">
      {(close) => (
        <>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] crm-ink">Mes RDV</p>
          <h2 className="mt-1 text-lg font-semibold">Proposer un rendez-vous</h2>
          <p className="mt-1 text-sm text-slate-500">Le client reçoit le mail Mes RDV. Le créneau entre dans l’agenda seulement s’il clique sur Accepter.</p>
          <p className="mt-1 truncate text-sm text-slate-500">{fiche.name} · {fiche.email || "Pas d’e-mail"}</p>
          <label className="mt-4 block text-xs font-semibold text-slate-500">Type d’événement</label>
          {types.length === 0 ? <p className="mt-1 text-sm text-slate-500">Aucun type dans Mes RDV. Créez-en un là-bas, puis revenez.</p> : (
            <select className="crm-field mt-1 w-full rounded-xl border px-3 py-2 text-sm" value={typeId} onChange={(event) => setTypeId(event.target.value)}>
              {types.map((row) => <option key={row.id} value={row.id}>{row.title}</option>)}
            </select>
          )}
          <p className="mb-1.5 mt-4 text-xs font-semibold text-slate-500">Jours ouverts</p>
          {loading && days.length === 0 ? <p className="text-sm text-slate-500">Chargement des disponibilités…</p> : null}
          {!loading && days.length === 0 && typeId ? <p className="text-sm text-slate-500">Aucun créneau ouvert sur les 30 prochains jours.</p> : null}
          <div className="crm-scroll flex gap-1.5 overflow-auto pb-1">
            {days.map((item) => (
              <button key={item.key} type="button" className={item.key === day ? "shrink-0 rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white" : "crm-pill shrink-0 rounded-full px-3 py-1 text-xs font-semibold"} onClick={() => pickDay(item.key)}>
                {dayLabel(item.key)}
              </button>
            ))}
          </div>
          <p className="mb-1.5 mt-3 text-xs font-semibold text-slate-500">Créneaux</p>
          {loading && day ? <p className="text-sm text-slate-500">Chargement des heures…</p> : null}
          {!loading && day && slots.length === 0 ? <p className="text-sm text-slate-500">Plus d’heure libre ce jour.</p> : null}
          <div className="flex flex-wrap gap-1.5">
            {slots.map((slot) => (
              <button key={slot} type="button" className={slot === time ? "rounded-full crm-fill px-3 py-1 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1 text-xs font-semibold"} onClick={() => setTime(slot)}>
                {slot}
              </button>
            ))}
          </div>
          {error ? <p className="mt-3 text-sm text-rose-700">{error}</p> : null}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="crm-pill rounded-full px-4 py-1.5 text-sm font-semibold" onClick={close}>Annuler</button>
            <button type="button" className="rounded-full crm-fill px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" disabled={!time} onClick={() => send(close)}>
              Envoyer la demande
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function dayLabel(key: string): string {
  const date = new Date(`${key}T12:00:00`);
  if (Number.isNaN(date.getTime())) return key;
  return date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}
